/** @format */
import { Choice } from '@mpb-com/feather/components/atoms/choice';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import debounce from 'lodash/debounce';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { type FilterOption } from '@toucan/client/features/search';
import { useSelectA11y } from '@toucan/client/features/search/hooks/use-select-a11y';

import { handleSelect } from './helpers/filters';
import { useKeyboardInteractions } from './hooks/use-keyboard-interactions';
import { styles } from './option-list.styles';
import { SearchFilterMenuOptionListProps } from './types';

const StyledSearchFilterMenuOptionList = styled.div`
    ${styles}
`;

export const SearchFilterMenuOptionList: FunctionComponent<
    SearchFilterMenuOptionListProps
> = ({
    filter,
    isLoading = false,
    displayOptionQuantity = true,
    selectedItems = [],
    setSelectedItems,
    autoFocus = true,
    className = '',
    showScrollFade = true,
    'data-testid': dataTestId = 'option-list',
}) => {
    const optionsRef = useRef<HTMLUListElement>(null);
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const [atScrollEnd, setAtScrollEnd] = useState(false);

    const { getItemProps } = useSelectA11y();

    const { activeIndex, setActiveIndex, clearActiveIndex } =
        useKeyboardInteractions(optionsRef, filter, onSelect);

    function isScrollAtBottom(): boolean {
        const element = optionsRef?.current;

        if (!element) {
            return false;
        }

        return Boolean(
            element.scrollHeight - (element.scrollTop + element.clientHeight) <=
            5
        );
    }

    const filterValuesWithQuantity =
        filter.values?.filter(
            (value: FilterOption) => (value.quantity ?? 0) > 0
        ) ?? [];

    useEffect(() => {
        if (!autoFocus || !optionsRef.current) {
            return;
        }

        const isAtScrollEnd = isScrollAtBottom();
        setAtScrollEnd(isAtScrollEnd);

        optionsRef.current?.focus();
        setActiveIndex(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [optionsRef?.current, isLoading]);

    function onListScroll() {
        const isAtScrollEnd = isScrollAtBottom();

        if (isAtScrollEnd !== atScrollEnd) {
            setAtScrollEnd(isAtScrollEnd);
        }
    }

    function onSelect(item: FilterOption) {
        setSelectedItems(handleSelect(item, selectedItems));
    }

    function isSelected(option: FilterOption): boolean {
        return selectedItems.some((item: FilterOption) => {
            return item.value === option.value;
        });
    }

    return (
        <StyledSearchFilterMenuOptionList
            atScrollEnd={!showScrollFade || atScrollEnd}
            className={className}
            data-testid={dataTestId}
        >
            <ul
                ref={optionsRef}
                tabIndex={0}
                onScroll={debounce(onListScroll, 150)}
                onBlur={() => {
                    clearActiveIndex();
                }}
            >
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {isLoading && (
                    <Skeleton variant={SkeletonVariant.TEXT} lineCount={2} />
                )}
                {!isLoading &&
                    filterValuesWithQuantity?.map(
                        (option: FilterOption, index: number) => {
                            const label = displayOptionQuantity
                                ? `${option.label} (${option.quantity})`
                                : option.label;

                            return (
                                <li
                                    key={option.value}
                                    data-active={index === activeIndex}
                                >
                                    <Choice
                                        tabIndex={-1}
                                        name={`${filter.label}.${index}`}
                                        label={label}
                                        value={option.value}
                                        disabled={
                                            option.quantity === 0 ||
                                            option.disabled
                                        }
                                        checked={isSelected(option)}
                                        onChange={() => {
                                            onSelect(option);
                                        }}
                                        {...getItemProps({
                                            key: option.value,
                                            item: option,
                                            isSelected: isSelected(option),
                                            index,
                                        })}
                                        key={option.value}
                                        className="choice"
                                    />
                                </li>
                            );
                        }
                    )}
            </ul>
        </StyledSearchFilterMenuOptionList>
    );
};
